import { useCallback } from 'react';
import { fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { z } from 'zod';

/** Backend plugin id, used by the `plugin://` protocol resolver. */
const DEVTOOLS_HUB_PLUGIN_ID = 'devtools-hub';

const copilotBudgetSchema = z.object({
  budgetLimit: z.number().nonnegative(),
  budgetUsed: z.number().nonnegative(),
  budgetTarget: z.string(),
  user: z.string(),
});

const copilotBudgetApiSchema = z.object({
  status: z.literal('ok'),
  results: z.array(
    z.object({
      budget_product_sku: z.string().optional(),
      budget_amount: z.number().optional(),
      consumed_amount: z.number().optional(),
      user: z.string().optional(),
    }),
  ),
});

export type CopilotBudget = z.infer<typeof copilotBudgetSchema>;

/**
 * Hook for fetching GitHub Copilot budget usage from DevTools Hub backend.
 *
 * Uses the `plugin://` protocol so the FetchApi's plugin-protocol middleware
 * resolves the DevTools Hub backend base URL via discovery automatically.
 * The response is validated against the expected schema.
 *
 * @returns An async function that returns typed budget data, or null when the user has no Copilot budget
 * @throws ZodError if the response doesn't match the expected schema
 * @throws Error if the network request fails
 */
export const useDevToolsHubCopilotBudget = () => {
  const fetchApi = useApi(fetchApiRef);

  return useCallback(
    async (userRef: string): Promise<CopilotBudget | null> => {
      const response = await fetchApi.fetch(
        `plugin://${DEVTOOLS_HUB_PLUGIN_ID}/copilot-budget/${encodeURIComponent(
          userRef,
        )}`,
      );

      if (!response.ok) {
        const statusText = response.statusText || `HTTP ${response.status}`;
        throw new Error(`Failed to fetch Copilot budget: ${statusText}`);
      }

      const { results } = copilotBudgetApiSchema.parse(await response.json());
      const [budget] = results;

      if (!budget) {
        return null;
      }

      return copilotBudgetSchema.parse({
        budgetTarget: budget.budget_product_sku ?? '',
        budgetLimit: budget.budget_amount ?? 0,
        budgetUsed: budget.consumed_amount ?? 0,
        user: budget.user ?? '',
      });
    },
    [fetchApi],
  );
};
